import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom, timeout, retry } from 'rxjs/operators';
import { defer, EMPTY, from, Observable, of } from 'rxjs';
import {
  AuthenticationActionTypes,
  loginFail,
  loginSuccess,
  mfaSetupRequired,
  mfaVerificationRequired,
  newPasswordRequired,
  userProfile,
} from '../actions/authentication.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { AccessControlPermissionsService } from '../../../api/services/access-control-permissions.service';
import { Store } from '@ngrx/store';
import { selectUserRole } from '../selector';
import { AuthenticatorService } from '../../service/authenticatorService';
import { MessageService } from 'primeng/api';
import { UserService } from '../../../api/services/user.service';
import { AmplifyService } from 'src/app/amplify/amplify.service';
import { TenantService } from '../../service/tenantService';
import { LoginReturnUrlService } from '../../service/login-return-url.service';
import { MfaChallengeService } from '../../service/mfa-challenge.service';
import { SupplierV1Service } from 'src/app/api/services';
import { TelemetryService } from 'src/app/shared/services/telemetry/telemetry.service';

const API_TIMEOUT = 15000;

@Injectable()
export class AuthenticationEffects {
  private readonly actions$ = inject(Actions);
  private readonly amplifyAuth = inject(AmplifyService);
  private readonly authenticatorService = inject(AuthenticatorService);
  private readonly permissionsService = inject(AccessControlPermissionsService);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly userService = inject(UserService);
  private readonly supplierServiceV1 = inject(SupplierV1Service);
  private readonly telemetry = inject(TelemetryService);
  private readonly mfaChallenge = inject(MfaChallengeService);
  private readonly tenantService = inject(TenantService);
  private readonly loginReturnUrl = inject(LoginReturnUrlService);

  constructor() {}

  private redirectFromQueryParams(): void {
    // Taken on every sign-in so a saved page is used at most once.
    const saved = this.loginReturnUrl.take();
    const fromQuery = this.route.snapshot.queryParams['returnUrl'];
    const returnUrl = fromQuery && fromQuery !== '/' ? fromQuery : saved ?? '/';
    this.authenticatorService
      .getRedirectUrlBasedOnRole(returnUrl)
      .pipe(take(1))
      .subscribe({
        next: (UrlTree) => {
          // Replacing the sign-in page keeps Back from bouncing through it, e.g. straight back into the doc hub.
          void this.router.navigateByUrl(UrlTree, { replaceUrl: true });
        },
        error: (err) => {
          console.error('Redirect error, going to default:', err);
          void this.router.navigate(['/']);
        },
      });
  }

  private loadSupplierV1$(supplierId: number | string) {
    return this.supplierServiceV1.getSupplierV1({ supplierId: String(supplierId) }).pipe(
      timeout(API_TIMEOUT),
      switchMap((supplier) => {
        if (supplier.active === false) {
          return this.handleDeactivatedUser();
        }
        this.redirectFromQueryParams();
        return of({ type: AuthenticationActionTypes.USER_SUPPLIER_V1, supplier });
      }),
      catchError((error) => {
        console.error('Failed to load supplier V1:', error);
        this.redirectFromQueryParams();
        return EMPTY;
      })
    );
  }

  private handleDeactivatedUser(): Observable<never> {
    return from(this.amplifyAuth.signOut()).pipe(
      tap(() => {
        globalThis.location.href = '/login?reason=deactivated';
      }),
      switchMap(() => EMPTY)
    );
  }

  private handleUserNotFoundInBackend(): Observable<never> {
    this.messageService.add({
      severity: 'error',
      summary: 'Account Not Found',
      detail: 'Your account is not registered in the system. Please contact your administrator.',
      life: 8000,
    });
    return from(this.amplifyAuth.signOut()).pipe(
      tap(() => this.router.navigate(['/login'])),
      switchMap(() => EMPTY)
    );
  }

  private handleUserProfileError(): Observable<never> {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load your profile. Please try again.',
      life: 5000,
    });
    return from(this.amplifyAuth.signOut()).pipe(
      tap(() => this.router.navigate(['/login'])),
      switchMap(() => EMPTY)
    );
  }

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthenticationActionTypes.LOGIN),
      tap(({ username }) => {
        // Set auth context BEFORE login attempt starts
        this.telemetry.setCustomProperty('authAction', 'login');
        this.telemetry.setCustomProperty('principal.identity', username);
      }),
      switchMap(({ username, password }) =>
        from(this.amplifyAuth.signIn(username, password)).pipe(
          timeout(API_TIMEOUT),
          filter((body) => {
            if (body.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
              this.store.dispatch(newPasswordRequired());
              this.telemetry.setCustomProperty('authAction', 'newPasswordRequired');
              return false;
            }
            if (body.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
              this.store.dispatch(mfaVerificationRequired());
              this.mfaChallenge.start();
              void this.router.navigate(['/mfa-verify', username]);
              this.telemetry.setCustomProperty('authAction', 'mfaVerificationRequired');
              return false;
            }
            return true;
          }),
          switchMap(() => from(this.amplifyAuth.fetchSession())),
          timeout(API_TIMEOUT),
          map((session) => loginSuccess({ token: String(session.tokens?.idToken ?? '') })),
          tap(() => {
            // Clear auth action after successful login
            this.telemetry.removeCustomProperty('authAction');
            // Note: principal.identity will be set again by setAuthenticatedUserContext after user profile loads
          }),
          map((response) => ({ type: AuthenticationActionTypes.LOGIN_SUCCESS, token: response.token })),
          catchError((error) => {
            console.error('Login error:', error);
            // Clear auth context on failure
            this.telemetry.removeCustomProperty('authAction');
            this.telemetry.removeCustomProperty('principal.identity');
            return of(loginFail({ error: this.getAuthErrorMessage(error) }));
          })
        )
      )
    );
  });

  // Added retry for slow Cognito responses during SSO callback
  ssoCallback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActionTypes.SSO_CALLBACK),
      switchMap(() =>
        from(this.amplifyAuth.fetchSession()).pipe(
          timeout(API_TIMEOUT),
          retry({ count: 2, delay: 1000 }),
          map((session) => {
            const token = session?.tokens?.idToken?.toString();
            if (!token) {
              throw new Error('No token received from SSO');
            }
            return loginSuccess({ token });
          }),
          map((response) => ({ type: AuthenticationActionTypes.LOGIN_SUCCESS, token: response.token })),
          catchError((error) => {
            console.error('SSO callback error:', error);
            return of(loginFail({ error: 'SSO authentication failed. Please try again.' }));
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthenticationActionTypes.LOGIN_SUCCESS),
      withLatestFrom(this.store.select(selectUserRole)),
      switchMap(([, userRole]) => {
        if (!userRole) {
          console.error('No user role after login success');
          return of(loginFail({ error: 'Invalid user session. Please try again.' }));
        }

        return this.permissionsService.listPermissionsByAccessControl({ accessControl: userRole }).pipe(
          timeout(API_TIMEOUT),
          map((response) => ({ type: AuthenticationActionTypes.USER_PERMISSIONS, userPermissions: response })),
          catchError((error) => {
            console.error('Permissions error:', error);
            if (error?.status === 403) {
              return this.handleDeactivatedUser();
            }
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load permissions. Please try again.',
            });
            return from(this.amplifyAuth.signOut()).pipe(
              tap(() => this.router.navigate(['/login'])),
              switchMap(() => EMPTY)
            );
          })
        );
      })
    );
  });

  loginFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthenticationActionTypes.LOGIN_FAIL),
        tap(({ error }) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: typeof error === 'string' ? error : 'Login failed. Please check your credentials.',
          });
        })
      ),
    { dispatch: false }
  );

  userPermissionAndSupplier$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActionTypes.USER_PERMISSIONS),
      switchMap(() =>
        this.userService.getOwnUser().pipe(
          timeout(API_TIMEOUT),
          catchError((error) => {
            console.error('Failed to get user profile:', error);

            // User authenticated in Cognito but doesn't exist in backend (404)
            if (error?.status === 404) {
              return this.handleUserNotFoundInBackend();
            }

            // Other errors (network, server errors, etc.)
            return this.handleUserProfileError();
          })
        )
      ),
      tap((user) => {
        if (user) {
          this.store.dispatch(userProfile({ userProfile: user }));
          // Set user context in Application Insights
          if (user.email) {
            this.telemetry.setAuthenticatedUserContext(user.email);
          }
        }
      }),
      switchMap((user) => {
        if (!user) {
          this.redirectFromQueryParams();
          return EMPTY;
        }

        return this.tenantService.isMfaRequiredForUser(user.email).pipe(
          take(1),
          timeout(API_TIMEOUT),
          switchMap((mfaRequiredForUser) => {
            if (mfaRequiredForUser && !user.mfaEnrolledAt) {
              this.store.dispatch(mfaSetupRequired());
              void this.router.navigate(['/mfa-setup']);
              return EMPTY;
            }

            const supplierId = user.supplierIdV1;
            if (supplierId === null || supplierId === undefined) {
              return defer(() => {
                this.redirectFromQueryParams();
                return EMPTY;
              });
            }
            return this.loadSupplierV1$(supplierId);
          }),
          catchError((error) => {
            console.error('MFA tenant config error:', error);
            this.redirectFromQueryParams();
            return EMPTY;
          })
        );
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthenticationActionTypes.LOGOUT),
        switchMap(() =>
          from(this.amplifyAuth.signOut()).pipe(
            tap(() => {
              // Clear all custom properties on logout
              this.telemetry.clearAuthenticatedUserContext();
              void this.router.navigate(['/login'], { queryParamsHandling: 'merge' });
            }),
            catchError((error: unknown) => {
              console.warn('Logout error (redirecting anyway):', error);
              this.telemetry.clearAuthenticatedUserContext();
              void this.router.navigate(['/login']);
              return EMPTY;
            })
          )
        )
      ),
    { dispatch: false }
  );

  // User-friendly error messages for common Cognito errors
  private getAuthErrorMessage(error: any): string {
    const code = error?.name || error?.code || '';
    const message = error?.message || '';

    if (code === 'NotAuthorizedException' || message.includes('Incorrect username or password')) {
      return 'Incorrect email or password.';
    }
    if (code === 'UserNotFoundException') {
      return 'No account found with this email.';
    }
    if (code === 'UserNotConfirmedException') {
      return 'Please verify your email before logging in.';
    }
    if (code === 'PasswordResetRequiredException') {
      return 'Password reset required. Please use the forgot password link.';
    }
    if (code === 'TooManyRequestsException' || message.includes('too many requests')) {
      return 'Too many attempts. Please wait a moment and try again.';
    }
    if (message.includes('Network')) {
      return 'Network error. Please check your connection.';
    }

    return 'Login failed. Please try again.';
  }
}
